import classNames from 'classnames';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import EmptyPlaceholder from '../../../components/empty-placholder';
import emptyPlaceholderIcon from '../../../assets/images/empty-data.svg';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchPurchaseQuote } from '../../../redux/purchase-quote';

interface CurrentPosition {
  latitude: number;
  longitude: number;
}
interface DeliveryProvidersProps {
  currentPosition: CurrentPosition | null;
}

function DeliveryProviders({ currentPosition }: DeliveryProvidersProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const purchaseQuote = useSelector((state: RootState) => state.purchaseQuote.data);
  const cartId = localStorage.getItem('cart_id');

  const handleProviderSelect = (providerId: number) => {
    dispatch(fetchPurchaseQuote({
      deliveryLat: currentPosition?.latitude,
      deliveryLon: currentPosition?.longitude,
      deliveryProviderId: providerId,
      promoCode: purchaseQuote?.promotion?.code,
      cartId,
    }));
  };

  if (purchaseQuote?.delivery?.delivery_providers?.length === 0) {
    return (
      <div className='bg-white shadow-sm rounded-2xl p-6 sm:p-8 animate-in fade-in slide-in-from-top-4 duration-500'>
        <h2 className='text-xl font-semibold text-gray-900 mb-6'>{t('checkout.deliveryProviders.title')}</h2>
        <EmptyPlaceholder
          title={t('checkout.deliveryProviders.unavailable.title')}
          subtitle={t('checkout.deliveryProviders.unavailable.message')}
          imageSrc={emptyPlaceholderIcon}
          fullHeight={false}
        />
      </div>
    );
  }

  return (
    <div>
      <h2 className='text-xl font-semibold text-gray-900 mb-4'>{t('checkout.deliveryProviders.title')}</h2>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
        {purchaseQuote?.delivery?.delivery_providers?.map((provider) => {
          const isSelected = purchaseQuote.delivery?.delivery_provider_id === provider.id;
          return (
            <div
              key={provider.id}
              className={classNames(
                'relative flex cursor-pointer rounded-xl border p-4 shadow-sm focus:outline-none transition-all duration-200 ease-in-out',
                {
                  'border-primary ring-2 ring-primary ring-offset-2 bg-primary/5': isSelected,
                  'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md': !isSelected,
                },
              )}
              onClick={() => handleProviderSelect(provider.id)}
              onKeyDown={(e) => e.key === 'Enter' && handleProviderSelect(provider.id)}
              role='button'
              tabIndex={0}
            >
              <span className='flex flex-1'>
                <span className='flex flex-col'>
                  <span className='flex flex-row items-center gap-3 mb-2'>
                    {provider.logo?.url && (
                      <img
                        src={provider.logo.url}
                        alt={provider.name}
                        className='h-8 w-8 object-contain rounded-full bg-gray-50'
                      />
                    )}
                    <span id='project-type-label' className='block text-sm font-medium text-gray-900'>
                      {provider.name}
                    </span>
                  </span>
                  {provider.short_description && (
                    <span id='project-type-description' className='mt-1 flex items-center text-sm text-gray-500'>
                      {provider.short_description}
                    </span>
                  )}
                </span>
              </span>
              {isSelected && (
                <CheckIcon className='h-5 w-5 text-primary absolute top-4 right-4 rtl:right-auto rtl:left-4' aria-hidden='true' />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DeliveryProviders;
